import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const norm = (s) => (s || "").toLowerCase().replace(/^(kota|kabupaten|kab\.?)\s+/, "").trim();

export const matchDestination = (destinations, a) => {
  const city = norm(a.city);
  const byCity = destinations.find((d) => norm(d.destination).startsWith(city + " (") || norm(d.destination) === city);
  return byCity || destinations.find((d) => norm(d.destination) === norm(a.province)) || null;
};

export const AddressAutocomplete = ({ onSelect }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const picked = useRef("");

  useEffect(() => {
    const term = q.trim();
    if (term.length < 3 || term === picked.current) { setItems([]); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true); setError("");
      try {
        const r = await api.get("/address/search", { params: { q: term }, signal: ctrl.signal });
        setItems(r.data); setOpen(true);
      } catch (e) {
        if (e?.code !== "ERR_CANCELED") setError("Pencarian alamat gagal, silakan isi manual di bawah.");
      } finally { setLoading(false); }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  const choose = (a) => {
    const label = `${a.village}, ${a.district}, ${a.city}`;
    picked.current = label;
    setQ(label); setItems([]); setOpen(false);
    onSelect(a);
  };

  return (
    <div className="relative space-y-1.5 sm:col-span-2">
      <Label htmlFor="addr-search">Cari kelurahan / kecamatan / kode pos</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input id="addr-search" autoComplete="off" className="pl-9" placeholder="Contoh: Rawa Burung atau 15215" value={q}
          data-testid="address-autocomplete-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (items[0]) choose(items[0]); } }} onChange={(e) => { picked.current = ""; setQ(e.target.value); }} onFocus={() => items.length && setOpen(true)} />
        {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-stone-400" />}
      </div>
      {error && <p className="text-xs text-red-700" data-testid="address-autocomplete-error">{error}</p>}
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-stone-200 bg-white shadow-lg" data-testid="address-suggestions">
          {items.map((a, i) => (
            <button type="button" key={`${a.postal_code}-${a.village}-${i}`} onClick={() => choose(a)} data-testid={`address-suggestion-${i}`}
              className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-amber-50">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <span><b>{a.village}</b>, Kec. {a.district}, {a.city}, {a.province} <span className="text-stone-500">{a.postal_code}</span></span>
            </button>
          ))}
        </div>
      )}
      {open && !loading && !error && q.trim().length >= 3 && items.length === 0 && q.trim() !== picked.current && (
        <p className="text-xs text-stone-500" data-testid="address-no-result">Alamat tidak ditemukan, silakan isi manual di bawah.</p>
      )}
    </div>
  );
};
